import {FACTIONS} from '../../constants/factions';
import {UNIT_TYPES} from '../units';
import {PHASES, WAR_PHASE_STAGES} from '../../constants/phases';
import {COMMON_BUILDING_TOKENS, SPECIAL_BUILDING_TOKENS} from '../../constants/tokens';
import {POLICIES} from '../../constants/policies';
import {CHESTS} from '../../constants/chests';
import {GROUND_BACKGROUNDS} from '../groundBackgrounds';
import {buildTutorialBarbaSteps, TUTORIAL_BARBA_PARTS} from './tutorialBarbaReplicas';

/**
 * Глава 3: рынок политик (Индустриализм), гильдии/храмы и пути влияния,
 * дороги как продолжение путей, катапульта (повреждение строений, ремонт —
 * описанием), крепости (недостройка при враге рядом, защита в ближнем бою,
 * разрушение обстрелом) и захват города Вентори.
 *
 * Стартует со снапшота public/fields/tutorial_chapter3.json (см. генератор
 * developer_artifacts/scripts/generateTutorialSnapshots.cjs): раунд 8, у
 * игрока два города и отряды d10 (0,1) и d6 (0,0), у Вентори форпост (2,-1)
 * с гарнизоном d6(6) на руднике (2,-2) и главный город (1,-4) на юге.
 *
 * Сценарий по раундам:
 *  Р8:  покупка Индустриализма (у Людей политика активна сразу — 3 действия),
 *       затем все три действия в деле: гильдия в нижнем городе (центр 0,3),
 *       вторая гильдия в верхнем (центр 0,0) — в одном городе два спецстроения
 *       одного типа не допускаются, подсветка ячеек это показывает — и храм.
 *       Путь влияния до форпоста уже есть (через дорогу 0,1 и ферму 1,1 к
 *       руднику 2,0). После построек — знакомство с инспекцией ячеек: клик
 *       по любой ячейке открывает панель ее описания (храм, затем склад).
 *  Р9:  две дороги (0,-1 и 0,-2) дотягивают пути влияния до главного города
 *       Вентори — доход гильдии и храма растет; бот строит СВОИ гильдию и
 *       храм (доход выше — фракционный бонус Вентори); найм катапульты и
 *       докупка ее размера до 3 — выстрелом она тут же уничтожает отряд на
 *       руинах.
 *  Р10: катапульта на казарме растет снабжением лишь на 1 (3 -> 4) — вне
 *       казармы не росла бы вовсе. Бот строит две крепости: №1 на руднике (2,0) — игрок подводит d10
 *       на пустую ячейку 1,0 над фермой (смежна руднику), и крепость не
 *       может достроиться; №2 на руднике (2,-2)
 *       под гарнизоном — достраивается. На центральном квартале крепость
 *       ставить нельзя было бы разрушить: урон по центру города игнорируется,
 *       пока целы базовые кварталы. Катапульта повреждает гильдию Вентори
 *       (поврежденное строение не работает до ремонта).
 *  Р11: ДОП. СНАБЖЕНИЕ (демо): штурмовик d6 получает два пайка (d10
 *       сознательно голодает); выстрел №1 по крепости (4 повр.); штурм с
 *       нарочно плохим броском (урон 1) -> игрок ПЕРЕБРАСЫВАЕТ кости за доп.
 *       снабжение -> урон 6: гарнизон -2, крепость +2 (итого 6), атакующий
 *       уничтожен ответным ударом целиком.
 *  Р12: бот гарнизон не снабжает (голод -2); два пайка — катапульте;
 *       выстрел добивает крепость (урон 4 -> ровно 10 повреждений), и
 *       гарнизон d10 (2 после голода) гибнет под обломками — выстрел бьет
 *       1 к 1 и по отряду, и по стенам; МАНЕВР
 *       (демо): катапульта стреляла и с доп. снабжением шагает на клетку
 *       дороги на этапе маневра.
 *  Р13: d10 встает на базовый квартал форпоста с гильдией (3,-1) — захват
 *       работает через ЛЮБОЙ квартал города; в конце раунда беззащитный
 *       форпост Вентори захвачен без боя.
 *
 * Формат шагов/ботскрипта — см. chapter1.js.
 */
export const TUTORIAL_CHAPTER_3 = {
  id: 'tutorial_chapter_3',
  title: 'Обучение: Глава 3',
  shortTitle: 'Глава 3',
  description: 'Политики, гильдии, храмы, крепости и катапульты',
  fieldPath: '/fields/tutorial_chapter3.json',
  groundBackground: GROUND_BACKGROUNDS.DESERT_2,
  playerFaction: FACTIONS.HUMANS,
  botFaction: FACTIONS.VENTORY,
  botName: 'Магистр Вентори',
  // Сценарий главы: раунды 8-14 (см. док-комментарий выше)
  finalRound: 16,
  // Стартовый кадр опускается заметно меньше обычного (см.
  // applyTutorialCameraShift): часть заданий главы — в ВЕРХНЕМ городе (вторая
  // гильдия на -1,0, дорога на север на 0,-2), и при обычной четверти высоты
  // они уезжали бы за верхний край. Стрелки главы лежат в полосе y от -208 до
  // +364 — этой доли хватает, чтобы полоса целиком помещалась в кадр начиная
  // с невысокой канвы (~700 px)
  cameraDownShiftFraction: 0.05,
  // Награда за ПЕРВОЕ прохождение главы (начисляет сервер)
  reward: {type: 'faction', factionId: FACTIONS.ORCS},
  // Тем, у кого Орки уже открыты, фракцию выдавать нечего — вместо нее сундук
  // (см. getTutorialChapterReward: расчет общий для сервера и для плашки главы)
  rewardIfFactionOwned: {type: 'chest', chestId: CHESTS.RANDOM_EPIC},
  ui: {
    hidePolicyMarket: false,
    hideInitiativeInterception: true,
    // Кнопка отмены знакома с главы 1 — видна сразу (отмена в обучении
    // все равно отключена, кнопка отвечает подсказкой)
    undoButtonRevealed: true,
  },

  steps: [
    // Реплики Барбы — цепочка окон до приветствия главы
    ...buildTutorialBarbaSteps('ch3'),
    {
      id: 'welcome',
      modalId: 'ch3_welcome',
    },
    {
      id: 'supply_r8',
      task: {
        text: 'Снабди оба отряда',
        arrowHexKey: '1,1',
      },
      allow: [{action: 'supply'}],
      complete: {
        check: 'allOf',
        checks: [
          {check: 'unitSuppliedAt', hexKey: '0,1'},
          {check: 'unitSuppliedAt', hexKey: '0,0'},
        ],
      },
    },
    {
      id: 'complete_supply_r8',
      task: {
        text: 'Заверши фазу Снабжения',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.CONSTRUCTION, currentPlayerColor: 'blue'},
    },
    {
      id: 'buy_industrialism',
      modalId: 'ch3_policy_market',
      task: {
        text: 'Открой рынок политик и купи Индустриализм',
        hint: 'Цена политики равна ее позиции на рынке',
        // Рынок открывает кнопка рынка в шапке (десктоп) или ярлык рынка у
        // левого края (телефон) — на экране всегда только одна из них
        arrowUiSelectorCandidates: [
          '.header-mine-zone_market-button',
          '.policy-side-menu-tab:not(.policy-side-menu-tab_own-policy)',
        ],
      },
      allow: [{action: 'buyPolicy', policyId: POLICIES.INDUSTRIALISM}],
      complete: {check: 'policyBought', color: 'blue', policyId: POLICIES.INDUSTRIALISM},
    },
    {
      // Где отображается активная политика: на кнопке открытия панели рынка
      // политик и в самой панели (см. Ch3PolicyShownModal)
      id: 'policy_display_info',
      modalId: 'ch3_policy_shown',
    },
    {
      // Полные правила доступны в любой момент: задание — открыть раздел
      // «Политики» (механика та же, что в главе 2 с разделом «Фракции»)
      id: 'open_rules_policies',
      modalId: 'ch3_rules_policies',
      task: {
        text: 'Открой Правила в меню матча (кнопка меню справа в шапке) и перейди в раздел «Политики»',
        arrowUiSelectorCandidates: [
          '.rules-modal_sections-panel--open [data-tutorial-select="rules_section_policies"]',
          '[data-tutorial-select="rules_sections_toggle"]',
          '[data-tutorial-select="in_game_menu_rules"]',
          '.app-header_menu-button',
        ],
        arrowAboveModals: true,
      },
      allow: [],
      // 'policies' = RULES_SECTION_IDS.POLICIES (литерал — как 'factions' в
      // шаге open_rules_factions главы 2)
      complete: {check: 'rulesSectionOpened', sectionId: 'policies'},
    },
    {
      // Как шаг scroll_rules_factions главы 2: подсказку и зачет прокрутки
      // ведет TutorialRulesScrollHint внутри открытых правил, поэтому у шага
      // нет ни modalId, ни стрелок
      id: 'scroll_rules_policies',
      task: {
        text: 'Пролистай страницу раздела «Политики» вниз — там описаны все политики',
        hint: 'Если правила закрыты, открой их в меню матча и вернись в раздел «Политики»',
      },
      allow: [],
      complete: {check: 'rulesSectionScrolled', sectionId: 'policies'},
    },
    {
      // Правила изучены — закрываем их и открываем список игроков: следующие
      // шаги (карточка политики противника) идут через него. Сама задача
      // видна на плашке справа вверху и без списка
      id: 'switch_to_players_panel',
      task: {
        text: 'Закрой правила и открой список игроков — кружки игроков в шапке',
        arrowUiSelectorCandidates: [
          '.rules-modal_close-button',
          '.header-players-row',
        ],
        arrowAboveModals: true,
      },
      allow: [],
      // 'players-list' = SIDE_MENU_IDS.PLAYERS_LIST (литерал — конфиг главы
      // грузится и сервером)
      complete: {check: 'sideMenuOnTop', sideMenuId: 'players-list'},
    },
    {
      id: 'build_guild',
      modalId: 'ch3_guild_influence',
      task: {
        text: 'Построй Гильдию на квартале южного города',
        arrowHexKey: '1,3',
      },
      allow: [{action: 'buildSpecial', token: SPECIAL_BUILDING_TOKENS.GUILD, hexKey: '1,3'}],
      complete: {check: 'specialBuildingAt', hexKey: '1,3', token: SPECIAL_BUILDING_TOKENS.GUILD},
    },
    {
      // Вторая гильдия — только в ДРУГОМ городе: в одном городе два
      // спецстроения одного типа не допускаются (подсветка доступных ячеек
      // покажет только верхний город). Второе действие Индустриализма.
      // Задержка модалки — после стройки первой гильдии играет анимация ее
      // дохода и путей влияния (как у шага храма)
      id: 'build_guild_second',
      modalId: 'ch3_guild_unique',
      modalDelayMs: 2600,
      task: {
        text: 'Построй вторую Гильдию — теперь в верхнем городе',
        arrowHexKey: '-1,0',
      },
      allow: [{action: 'buildSpecial', token: SPECIAL_BUILDING_TOKENS.GUILD, hexKey: '-1,0'}],
      complete: {check: 'specialBuildingAt', hexKey: '-1,0', token: SPECIAL_BUILDING_TOKENS.GUILD},
    },
    {
      id: 'build_temple',
      modalId: 'ch3_temple',
      // После стройки гильдии проигрывается анимация ее дохода и путей
      // влияния (таймаут 1с + сама анимация) — модалку храма не торопим
      modalDelayMs: 2600,
      task: {
        text: 'Построй Храм рядом со второй гильдией — в верхнем городе',
        arrowHexKey: '-1,1',
      },
      allow: [{action: 'buildSpecial', token: SPECIAL_BUILDING_TOKENS.TEMPLE, hexKey: '-1,1'}],
      complete: {check: 'specialBuildingAt', hexKey: '-1,1', token: SPECIAL_BUILDING_TOKENS.TEMPLE},
    },
    {
      // Инспекция ячеек: клик по любой ячейке вне подсветки действий
      // открывает панель ее подробного описания. Задержка модалки — после
      // стройки храма играет анимация его дохода
      id: 'inspect_temple',
      modalId: 'ch3_cell_info',
      modalDelayMs: 2600,
      task: {
        text: 'Нажми на ячейку с храмом и посмотри её подробное описание',
        arrowHexKey: '-1,1',
      },
      allow: [],
      complete: {check: 'hexInspected', hexKey: '-1,1'},
    },
    {
      id: 'inspect_warehouse',
      task: {
        text: 'Теперь посмотри ячейку со складом — в нижнем городе',
        arrowHexKey: '1,2',
      },
      allow: [],
      complete: {check: 'hexInspected', hexKey: '1,2'},
    },
    {
      id: 'complete_construction_r8',
      task: {
        text: 'Заверши фазу Строительства',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.WAR, currentPlayerColor: 'blue'},
    },
    {
      id: 'skip_war_r8',
      task: {
        text: 'В бой пока не пойдем — заверши этапы фазы Войны',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.SUPPLY, minRound: 9, currentPlayerColor: 'blue'},
    },
    {
      // Вентори купили политику в свою фазу Строительства Р8 (см. botScript
      // '8|construction|') — знакомимся с чужими политиками
      id: 'enemy_policy_card',
      modalId: 'ch3_enemy_policy',
      task: {
        text: 'Открой карточку политики противника: нажми на плашку политики в списке игроков, затем закрой карточку',
        // Плашка политики видна только при открытом списке игроков: если он
        // закрыт или его накрыл рынок, проверка перекрытия отклонит плашку, и
        // стрелка поведет через строку игроков в шапке (а при открытом
        // рынке — сперва через его кнопку закрытия, это generic-редирект
        // оверлеев)
        arrowUiSelectorCandidates: [
          '[data-tutorial-select="policy_card_close"]',
          '.player-list .player-info:last-child .player-info_policy',
          '.header-players-row',
        ],
        arrowAboveModals: true,
      },
      allow: [],
      complete: {check: 'policyCardViewedFrom', sources: ['players_panel']},
    },
    {
      id: 'supply_r9',
      task: {
        text: 'Снабди оба отряда',
        arrowHexKey: '1,1',
      },
      allow: [{action: 'supply'}],
      complete: {
        check: 'allOf',
        checks: [
          {check: 'unitSuppliedAt', hexKey: '0,1'},
          {check: 'unitSuppliedAt', hexKey: '0,0'},
        ],
      },
    },
    {
      id: 'complete_supply_r9',
      task: {
        text: 'Заверши фазу Снабжения',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.CONSTRUCTION, minRound: 9, currentPlayerColor: 'blue'},
    },
    {
      id: 'build_road_1',
      modalId: 'ch3_road_income',
      task: {
        text: 'Проложи дорогу на север от своего города',
        arrowHexKey: '0,-1',
      },
      allow: [{action: 'build', token: COMMON_BUILDING_TOKENS.ROAD, hexKey: '0,-1'}],
      complete: {check: 'hexToken', hexKey: '0,-1', token: COMMON_BUILDING_TOKENS.ROAD},
    },
    {
      id: 'build_road_2',
      task: {
        text: 'Продли дорогу еще на одну ячейку — до главного города Вентори станет ближе',
        arrowHexKey: '0,-2',
      },
      allow: [{action: 'build', token: COMMON_BUILDING_TOKENS.ROAD, hexKey: '0,-2'}],
      complete: {check: 'hexToken', hexKey: '0,-2', token: COMMON_BUILDING_TOKENS.ROAD},
    },
    {
      id: 'complete_construction_r9',
      task: {
        text: 'Заверши фазу Строительства',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      // Шаг закрывается началом фазы Строительства БОТА: следующий шаг —
      // карточка политики с кнопки завершения хода (доступна только в ход
      // противника)
      complete: {
        check: 'gameMoment',
        phase: PHASES.CONSTRUCTION,
        minRound: 9,
        // Цвет Вентори в снапшоте главы — оранжевый (см. fields/tutorial_chapter3.json)
        currentPlayerColor: 'orange',
      },
    },
    {
      // Второй способ открыть карточку политики — в ход противника: его
      // политика на плашке ходящего под его кружком в шапке. Бот ждет этот шаг
      // (waitTutorialStepPassed в '9|construction|'). Id шага прежний — по нему
      // его ждет сценарий бота
      id: 'policy_card_from_button',
      task: {
        text: 'Пока Вентори ходят — нажми на иконку их политики Олигархия на плашке под их кружком в шапке для ознакомления с ней',
        arrowUiSelectorCandidates: [
          '[data-tutorial-select="policy_card_close"]',
          '[data-tutorial-select="acting_badge_policy"]',
        ],
        arrowAboveModals: true,
        duringOpponentTurn: true,
      },
      allow: [],
      complete: {check: 'policyCardViewedFrom', sources: ['acting_badge']},
    },
    {
      // Бот достраивает гильдию и храм — модалка про них покажется уже в
      // свой ход (следующий шаг)
      id: 'wait_ventory_construction',
      task: {
        text: 'Дождись окончания хода противника',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        minRound: 9,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'ventory_built',
      modalId: 'ch3_ventory_bonus',
    },
    {
      id: 'recruit_catapult',
      modalId: 'ch3_catapult',
      task: {
        text: 'Найми Катапульту: выбери её в панели снизу и нажми на казарму',
        arrowHexKey: '0,2',
      },
      allow: [{action: 'recruitUnit', unitType: UNIT_TYPES.D4.id, hexKey: '0,2'}],
      complete: {check: 'unitRecruitedAt', hexKey: '0,2', type: UNIT_TYPES.D4.id},
    },
    {
      // Докупки только две (размер 3, не максимум 4): последний размер
      // катапульта добирает снабжением на казарме в Р10 — урок «катапульта
      // растет только на 1 и только на казарме» (шаг supply_r10)
      id: 'size_catapult',
      task: {
        text: 'Докупи катапульте размер дважды (до 3) — последний размер она доберёт снабжением на казарме',
        arrowHexKey: '0,2',
      },
      // Нанятая катапульта автовыбирается — сбрасываем ради ручного выбора
      deselectsUnitOnEntry: true,
      allow: [{action: 'sizeRecruitment'}],
      complete: {check: 'unitSizeRecruitedAt', hexKey: '0,2', count: 2},
    },
    {
      // Катапульта сразу в деле: выстрел уничтожает отряд Вентори на руинах
      // в том же раунде, в котором она нанята. Отряд стартует размером 1 и
      // к выстрелу откормлен ботом ровно до 3 (см. генератор снапшота).
      // Кости [0,0]: катапульта не двигалась — лучший бросок, урон =
      // размер 3 + 0 = 3 (>= 3).
      // Отряд снимается с поля при завершении этапа стрельбы (применение
      // pending-выстрелов) — на этом и закрывается предикат noUnitAt
      id: 'shoot_ruins_unit',
      dice: {damage: [0, 0]},
      task: {
        text: 'Заверши найм и перемещение, затем выстрели катапультой по отряду Вентори на руинах',
        arrowHexKey: '4,-2',
        arrowUiSelector: '.complete-phase-button',
        arrowHexKeyStage: WAR_PHASE_STAGES.SHOOTING,
      },
      // Выстрел обязателен — иначе шаг не завершится уже никогда, а бот
      // в фазе Войны Р10 ждет шага, до которого сценарию не дойти
      allow: [
        {action: 'completeTurn', requireObjectiveInStage: WAR_PHASE_STAGES.SHOOTING},
        {action: 'shoot', targetHexKey: '4,-2', fromHexKey: '0,2'},
      ],
      complete: {check: 'noUnitAt', hexKey: '4,-2'},
    },
    {
      id: 'complete_war_r9',
      task: {
        text: 'Заверши этапы фазы Войны',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.SUPPLY, minRound: 10, currentPlayerColor: 'blue'},
    },
    {
      // Рост катапульты: на казарме +1 (3 -> 4, максимум к выстрелу по
      // гильдии в этом же раунде), а не +2, как у остальных отрядов
      id: 'supply_r10',
      modalId: 'ch3_catapult_growth',
      task: {
        text: 'Снабди все три отряда — катапульта на казарме вырастет лишь на 1',
        arrowHexKey: '1,1',
      },
      allow: [{action: 'supply'}],
      complete: {
        check: 'allOf',
        checks: [
          {check: 'unitSuppliedAt', hexKey: '0,1'},
          {check: 'unitSuppliedAt', hexKey: '0,0'},
          {check: 'unitSuppliedAt', hexKey: '0,2'},
        ],
      },
    },
    {
      id: 'complete_supply_r10',
      task: {
        text: 'Заверши фазу Снабжения',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.CONSTRUCTION, minRound: 10, currentPlayerColor: 'blue'},
    },
    {
      id: 'skip_construction_r10',
      task: {
        text: 'Строить в этом раунде ничего не будем — заверши фазу Строительства',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.RECRUITMENT,
        minRound: 10,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'block_fortress',
      modalId: 'ch3_fortress',
      // Пустая ячейка НАД фермой (1,0): она тоже смежна руднику 2,0 и не
      // загораживает жетон фермы; снабжение d10 в Р12 дотянется — пустая
      // ячейка может быть конечной точкой пути (ферма 1,1 смежна)
      task: {
        text: 'Заверши найм и подведи Тяжелую пехоту к строящейся крепости на руднике',
        hint: 'Крепость не достроится в конце раунда, если рядом стоит враг',
        arrowHexKey: '1,0',
        arrowUiSelector: '.complete-phase-button',
        arrowHexKeyStage: WAR_PHASE_STAGES.MOVEMENT,
      },
      // Перемещение обязательно: пропустив его, игрок запирает сценарий на
      // этом шаге, а бот в своей фазе Войны Р10 ждет шага complete_war_r10
      allow: [
        {action: 'completeTurn', requireObjectiveInStage: WAR_PHASE_STAGES.MOVEMENT},
        {action: 'moveUnit', fromHexKey: '0,1', toHexKey: '1,0'},
      ],
      complete: {check: 'unitMovedTo', toHexKey: '1,0', color: 'blue'},
    },
    {
      id: 'shoot_guild',
      // Кости стрельбы: катапульта не двигалась — берется лучший бросок.
      // Урон = размер 4 + 0 = 4 (>= 4) -> одно повреждение гильдии
      dice: {damage: [0, 0]},
      task: {
        text: 'Заверши перемещение и выстрели катапультой по гильдии Вентори',
        // Подсказка идет в тост при запрещенном действии (см. tutorialMiddleware):
        // объясняет, почему кнопка завершения хода на стрельбе не срабатывает
        hint: 'Выстрел по гильдии — часть сценария: пропустить этап стрельбы нельзя',
        arrowHexKey: '3,-1',
        arrowUiSelector: '.complete-phase-button',
        arrowHexKeyStage: WAR_PHASE_STAGES.SHOOTING,
      },
      // Выстрел обязателен: пропустив его, игрок запирал игру намертво —
      // шаг не завершался (гильдия цела), а бот в своей фазе Войны Р10 ждет
      // шага complete_war_r10, до которого сценарию уже не дойти
      allow: [
        {action: 'completeTurn', requireObjectiveInStage: WAR_PHASE_STAGES.SHOOTING},
        {action: 'shoot', targetHexKey: '3,-1', fromHexKey: '0,2'},
      ],
      complete: {check: 'buildingDamageAtLeast', hexKey: '3,-1', min: 1},
    },
    {
      id: 'complete_war_r10',
      modalId: 'ch3_damaged_building',
      task: {
        text: 'Заверши этапы фазы Войны',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.SUPPLY, minRound: 11, currentPlayerColor: 'blue'},
    },
    {
      id: 'supply_r11',
      modalId: 'ch3_additional_supply',
      // Демо дополнительного снабжения: d6-штурмовик получает ДВА пайка
      // (второй = доп. снабжение, потратим на переброс костей штурма), d10
      // сознательно голодает — пайков всего три
      task: {
        text: 'Дай обычной Пехоте два пайка и покорми Катапульту — Тяжёлая пехота сегодня потерпит',
        arrowHexKey: '0,0',
      },
      allow: [
        // Пехоте нужен ВТОРОЙ паёк — доп. снабжение здесь задумано сценарием
        {action: 'supply', unitHexKey: '0,0', additional: true},
        {action: 'supply', unitHexKey: '0,2'},
      ],
      complete: {
        check: 'allOf',
        checks: [
          {check: 'unitSuppliedAt', hexKey: '0,0', count: 2},
          {check: 'unitSuppliedAt', hexKey: '0,2'},
        ],
      },
    },
    {
      id: 'complete_supply_r11',
      task: {
        text: 'Заверши снабжение и строительство',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.RECRUITMENT,
        minRound: 11,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'assault_move',
      modalId: 'ch3_fortress_assault',
      task: {
        text: 'Заверши найм и подведи обычную Пехоту вплотную к достроенной крепости',
        arrowHexKey: '1,-1',
        arrowUiSelector: '.complete-phase-button',
        arrowHexKeyStage: WAR_PHASE_STAGES.MOVEMENT,
      },
      allow: [{action: 'completeTurn'}, {action: 'moveUnit', fromHexKey: '0,0', toHexKey: '1,-1'}],
      complete: {check: 'unitMovedTo', toHexKey: '1,-1', color: 'blue'},
    },
    {
      id: 'engage_assault',
      // Очередь костей: выстрел по крепости [0,-2] — катапульта не
      // двигалась, берется ЛУЧШИЙ бросок, урон 4+0=4 (разные кости — наглядно
      // для модалки ch3_catapult_vs_fortress); затем расчет штурма — d6
      // бросает ПЛОХО [-3,-2] (урон 6-5=1, крепость поглотила), гарнизон
      // [0,0] (урон 6+0+0=6 — ровно столько, чтобы снести Пехоту игрока
      // размера 6, как обещает модалка ch3_fortress_defense). Гарнизон к
      // этому моменту размера 6 (10 минус 4 от выстрела): кости урона не
      // дают больше 0, поэтому нужные 6 урона — это сам размер гарнизона
      // (см. генератор снапшота: стартует с 6 и растет до 10 к выстрелу).
      // Плохой бросок игрока — заготовка под демо переброса. Кости глобальной
      // очереди переживают смену шага — штурмовые значения потребятся уже на
      // следующих шагах
      dice: {damage: [0, -2, -3, -2, 0, 0]},
      task: {
        text: 'Заверши перемещение и выстрели по крепости',
        arrowHexKey: '2,-2',
        arrowUiSelector: '.complete-phase-button',
        arrowHexKeyStage: WAR_PHASE_STAGES.SHOOTING,
      },
      allow: [{action: 'completeTurn'}, {action: 'shoot', targetHexKey: '2,-2', fromHexKey: '0,2'}],
      // Завершается САМИМ выстрелом (pending), еще на этапе стрельбы —
      // модалка следующего шага выходит с задержкой после выстрела (см. ее
      // modalDelayMs), а не после завершения этапа
      complete: {check: 'shotPendingAt', targetHexKey: '2,-2'},
    },
    {
      // Выстрел уже виден на поле (pending): объясняем, почему катапульта
      // хороша и против отрядов в крепости, и как посчитался урон 4 по костям
      // [0,-2]. До показа модалки стрелка на кнопку завершения этапа не
      // показывается (гашение подсказок до модалки шага), после закрытия —
      // ведет завершить этап стрельбы
      id: 'catapult_vs_fortress',
      modalId: 'ch3_catapult_vs_fortress',
      // Валун летит до крепости около секунды, и итог выстрела («−4», линия)
      // появляется на поле только с попаданием (см. configs/shotAnimation).
      // С дефолтной секундной задержкой модалка накрывала поле в момент удара
      // — даем удару и его вспышке отыграть. Дольше ждать незачем: итог
      // останется на поле и после закрытия модалки, а пока ее нет, игрок
      // может завершить этап и пропустить объяснение
      modalDelayMs: 1400,
      task: {
        text: 'Заверши этап стрельбы — начнётся штурм',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.COMBAT,
        minRound: 11,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'reroll_assault',
      modalId: 'ch3_reroll',
      // Кости переброса: обе новые кости d6 по 0 -> урон 6 (равный гарнизону)
      dice: {damage: [0, 0]},
      // Цепочка подсказок: отряд в бою не выбран -> ячеечная стрелка на него
      // (task.meleeUnitHexKey); выбран -> стрелки над КАЖДОЙ еще не выбранной
      // костью переброса (arrowUiSelectorMultiple); обе выбраны -> стрелка на
      // кнопку переброса. Селекторы скоуплены маркером владельца панели боя —
      // на панель чужого отряда стрелки не целятся
      task: {
        text: 'Бросок слаб! Нажми на свой отряд в бою, выбери обе кости и перебрось их',
        hint: 'Переброс доступен отряду с дополнительным снабжением',
        meleeUnitHexKey: '1,-1',
        arrowUiSelectorCandidates: [
          '[data-tutorial-melee-owner="own"] .melee-toolbar_stats-container .damage-dice:not(.damage-dice-selected)',
          '[data-tutorial-melee-owner="own"] .reroll-button',
        ],
        arrowUiSelectorMultiple: true,
      },
      // Перебросить нужно обе кости: переброс одной дал бы урон меньше
      // гарнизона, и штурм не удался бы (панель боя объясняет это тостом)
      allow: [{action: 'meleeInteraction', rerollDiceCount: 2}],
      complete: {check: 'meleeRerolledBy', color: 'blue'},
    },
    {
      id: 'resolve_assault',
      modalId: 'ch3_fortress_defense',
      task: {
        text: 'Изучи итог штурма и заверши этапы хода',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}, {action: 'meleeInteraction'}],
      complete: {check: 'gameMoment', phase: PHASES.SUPPLY, minRound: 12, currentPlayerColor: 'blue'},
    },
    {
      id: 'supply_r12',
      // Теперь два пайка — катапульте: доп. снабжение потратим на маневр
      // после выстрела
      task: {
        text: 'Покорми Тяжёлую пехоту и дай Катапульте два пайка — второй пригодится для манёвра',
        arrowHexKey: '0,2',
      },
      allow: [
        {action: 'supply', unitHexKey: '1,0'},
        // Катапульте нужен ВТОРОЙ паёк (пойдёт на манёвр) — доп. снабжение
        // здесь задумано сценарием
        {action: 'supply', unitHexKey: '0,2', additional: true},
      ],
      complete: {
        check: 'allOf',
        checks: [
          {check: 'unitSuppliedAt', hexKey: '1,0'},
          {check: 'unitSuppliedAt', hexKey: '0,2', count: 2},
        ],
      },
    },
    {
      id: 'complete_supply_r12',
      task: {
        text: 'Заверши снабжение и строительство',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.RECRUITMENT,
        minRound: 12,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'shoot_fortress_final',
      // Кости [0,0]: урон 4+0=4 — крепости хватает (6+4 = 10, разрушена),
      // а гарнизон d10 (2: в Р12 бот его не снабжает, голод -2) гибнет под
      // обломками — выстрел бьет 1 к 1 и по отряду в крепости, и по стенам
      dice: {damage: [0, 0]},
      task: {
        text: 'Заверши найм и перемещение, затем добей крепость выстрелом катапульты',
        arrowHexKey: '2,-2',
        arrowUiSelector: '.complete-phase-button',
        arrowHexKeyStage: WAR_PHASE_STAGES.SHOOTING,
      },
      allow: [{action: 'completeTurn'}, {action: 'shoot', targetHexKey: '2,-2', fromHexKey: '0,2'}],
      complete: {check: 'specialBuildingGone', hexKey: '2,-2'},
    },
    {
      id: 'maneuver_catapult',
      modalId: 'ch3_maneuver',
      task: {
        text: 'Этап манёвра: сманеврируй катапультой на клетку дороги',
        arrowHexKey: '0,1',
      },
      allow: [{action: 'completeTurn'}, {action: 'maneuverUnit', fromHexKey: '0,2', toHexKey: '0,1'}],
      complete: {check: 'unitManeuveredTo', toHexKey: '0,1', color: 'blue'},
    },
    {
      id: 'complete_war_r12',
      modalId: 'ch3_fortress_destroyed',
      task: {
        text: 'Заверши этапы фазы Войны',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.SUPPLY, minRound: 13, currentPlayerColor: 'blue'},
    },
    {
      id: 'supply_r13',
      task: {
        text: 'Снабди Тяжёлую пехоту и Катапульту',
        arrowHexKey: '1,1',
      },
      allow: [
        {action: 'supply', unitHexKey: '1,0'},
        {action: 'supply', unitHexKey: '0,1'},
      ],
      complete: {
        check: 'allOf',
        checks: [
          {check: 'unitSuppliedAt', hexKey: '1,0'},
          {check: 'unitSuppliedAt', hexKey: '0,1'},
        ],
      },
    },
    {
      id: 'complete_supply_r13',
      task: {
        text: 'Заверши снабжение и строительство',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.RECRUITMENT,
        minRound: 13,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'capture_outpost',
      modalId: 'ch3_capture_city',
      // Гарнизон погиб под обломками крепости еще в Р12 — форпост беззащитен.
      // Захват работает через ЛЮБОЙ квартал города: d10 (скорость 2) идет с
      // 1,0 сразу на БАЗОВЫЙ квартал с гильдией (3,-1) — так урок про «любой
      // квартал» запоминается лучше, чем ход на центральный
      task: {
        text: 'Заверши найм и поставь Тяжелую пехоту на квартал с гильдией Вентори',
        arrowHexKey: '3,-1',
        arrowUiSelector: '.complete-phase-button',
        arrowHexKeyStage: WAR_PHASE_STAGES.MOVEMENT,
      },
      allow: [{action: 'completeTurn'}, {action: 'moveUnit', fromHexKey: '1,0', toHexKey: '3,-1'}],
      complete: {check: 'unitMovedTo', toHexKey: '3,-1', color: 'blue'},
    },
    {
      id: 'finish_chapter',
      task: {
        text: 'Заверши оставшиеся этапы — в конце раунда форпост перейдёт к тебе',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'cityOwnedBy', centralHexKey: '2,-1', color: 'blue'},
    },
    // Напутствие Барбы после последнего задания обучения: глава засчитывается
    // закрытием последнего окна, следом — поздравление с кнопкой «В битву»
    ...buildTutorialBarbaSteps('ch3', TUTORIAL_BARBA_PARTS.OUTRO),
  ],

  botScript: {
    '8|supply|': [
      {op: 'autoSupply'},
    ],
    '8|construction|': [
      // Вентори покупают себе политику — игрок знакомится с чужими
      // политиками в начале Р9 (шаг enemy_policy_card). Олигархия: торговая
      // фракция и по духу, и по тому, что глава показывает гильдии
      {op: 'buyPolicy', policyId: POLICIES.OLIGARCHY},
    ],
    '9|supply|': [
      {op: 'autoSupply'},
    ],
    '9|construction|': [
      // Пауза: игрок открывает карточку политики бота с плашки ходящего в
      // шапке (шаг policy_card_from_button)
      {op: 'waitTutorialStepPassed', stepId: 'policy_card_from_button'},
      {op: 'buildSpecial', token: SPECIAL_BUILDING_TOKENS.GUILD, hexKey: '3,-1'},
      {op: 'buildSpecial', token: SPECIAL_BUILDING_TOKENS.TEMPLE, hexKey: '3,-2'},
    ],
    '10|supply|': [
      {op: 'autoSupply'},
    ],
    // Своя фаза Войны Р10 у бота начинается только после того, как игрок
    // прочитал модалку «Поврежденное строение» (шаг complete_war_r10) —
    // иначе завершение хода бота наслаивает свои модалки поверх нее
    '10|war|recruitment': [
      {op: 'waitTutorialModalClosed', stepId: 'complete_war_r10'},
    ],
    '10|construction|': [
      {op: 'buildSpecial', token: SPECIAL_BUILDING_TOKENS.FORTRESS, hexKey: '2,0'},
      {op: 'buildSpecial', token: SPECIAL_BUILDING_TOKENS.FORTRESS, hexKey: '2,-2'},
    ],
    '11|supply|': [
      {op: 'autoSupply'},
    ],
    // Р12: бот гарнизон не снабжает — голод -2 (4 -> 2), и последний выстрел
    // катапульты (урон 4: кости не дают больше 0) его добивает. Других
    // отрядов у Вентори к этому раунду нет. Пустой ключ: бот сразу завершает
    // фазу снабжения
    '12|supply|': [],
    '13|supply|': [
      {op: 'autoSupply'},
    ],
    '14|supply|': [
      {op: 'autoSupply'},
    ],
  },
};
